import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandSchema, defaultSeoSchema, footerSchema, iconKeys, navigationSchema, type Brand, type DefaultSeo, type Footer, type Navigation, type SettingKey } from '@agency/contracts';
import { Button, Notice, SelectField, Skeleton, TextAreaField, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { CheckField, errText, issueMap, LinkListEditor, moveItem, orUndef, RowControls, serverErrors, type Errors, type LinkRow } from './common.tsx';

type Ctx<D> = { draft: D; set: (d: D) => void; errors: Errors; readOnly: boolean };
type Schema<V> = { safeParse: (v: unknown) => { success: true; data: V } | { success: false; error: { issues: readonly { path: readonly PropertyKey[]; message: string }[] } } };

/**
 * One editor for every site setting group. The draft is plain strings so empty optional inputs are possible while typing;
 * it is converted, validated with the shared contract schema and only then sent. The API replaces the whole group (no revision token).
 */
function SettingEditor<V, D>({ settingKey, title, intro, schema, toDraft, fromDraft, children }: {
  settingKey: SettingKey; title: string; intro: string; schema: Schema<V>; toDraft: (v: V) => D; fromDraft: (d: D) => unknown; children: (c: Ctx<D>) => ReactNode;
}) {
  const q = useQuery<V, ApiClientError>({ queryKey: ['site-setting', settingKey], queryFn: () => get(`/admin/site/settings/${settingKey}`), refetchOnWindowFocus: false });
  const headingId = `setting-${settingKey}-h`;
  return (
    <section className="panel" aria-labelledby={headingId}>
      <h2 id={headingId}>{title}</h2>
      <p className="ag-muted">{intro}</p>
      {q.isLoading ? <Skeleton width="100%" height={200} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : (
        <SettingForm settingKey={settingKey} title={title} initial={q.data!} schema={schema} toDraft={toDraft} fromDraft={fromDraft}>{children}</SettingForm>
      )}
    </section>
  );
}

function SettingForm<V, D>({ settingKey, title, initial, schema, toDraft, fromDraft, children }: {
  settingKey: SettingKey; title: string; initial: V; schema: Schema<V>; toDraft: (v: V) => D; fromDraft: (d: D) => unknown; children: (c: Ctx<D>) => ReactNode;
}) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const readOnly = !can('settings.manage');
  const [saved, setSaved] = useState(initial);
  const [draft, set] = useState<D>(() => toDraft(initial));
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(saved));

  const save = useMutation({
    mutationFn: (body: V) => put<V>(`/admin/site/settings/${settingKey}`, body),
    onSuccess: (v) => { setSaved(v); set(toDraft(v)); qc.setQueryData(['site-setting', settingKey], v); toast({ tone: 'success', message: `${title} saved.` }); },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });
  function submit() {
    setError(null);
    const p = schema.safeParse(fromDraft(draft));
    if (!p.success) { setErrors(issueMap(p.error.issues)); setError('Some fields need attention. Fix the highlighted fields and save again.'); return; }
    setErrors({});
    save.mutate(p.data);
  }
  return (
    <form noValidate className="formgrid formgrid--wide" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title={`Could not save ${title.toLowerCase()}`}>{error}</Notice> : null}
      {readOnly ? <Notice tone="info" title="Read only" live={false}>Your role can view these settings but not change them.</Notice> : null}
      {children({ draft, set, errors, readOnly })}
      {!readOnly ? (
        <div className="actions__row">
          <Button type="submit" variant="primary" loading={save.isPending}>Save {title.toLowerCase()}</Button>
          <Button disabled={!dirty || save.isPending} onClick={() => { set(toDraft(saved)); setErrors({}); setError(null); }}>Discard changes</Button>
          <span className="savebar" role="status">{dirty ? 'Unsaved changes' : 'Saved'}</span>
        </div>
      ) : null}
    </form>
  );
}

const str = (s: string | undefined) => s ?? '';

// ---------- Brand ----------
type BrandDraft = { brandName: string; legalName: string; logoUrl: string; faviconUrl: string; defaultOgImageUrl: string; primaryContactEmail: string; phone: string; address: string; socialLinks: { network: string; url: string }[] };
export function BrandForm() {
  return (
    <SettingEditor<Brand, BrandDraft>
      settingKey="brand" title="Brand" intro="Name, contact details and default images used across the public site. Nothing about the agency is hard-coded in pages."
      schema={brandSchema}
      toDraft={(b) => ({ brandName: b.brandName, legalName: str(b.legalName), logoUrl: str(b.logoUrl), faviconUrl: str(b.faviconUrl), defaultOgImageUrl: str(b.defaultOgImageUrl), primaryContactEmail: str(b.primaryContactEmail), phone: str(b.phone), address: str(b.address), socialLinks: b.socialLinks })}
      fromDraft={(d) => ({ brandName: d.brandName, legalName: orUndef(d.legalName), logoUrl: orUndef(d.logoUrl), faviconUrl: orUndef(d.faviconUrl), defaultOgImageUrl: orUndef(d.defaultOgImageUrl), primaryContactEmail: orUndef(d.primaryContactEmail), phone: orUndef(d.phone), address: orUndef(d.address), socialLinks: d.socialLinks })}
    >
      {({ draft: d, set, errors: e, readOnly }) => {
        const f = (k: keyof Omit<BrandDraft, 'socialLinks'>) => ({ value: d[k], readOnly, error: e[k], onChange: (ev: { target: { value: string } }) => set({ ...d, [k]: ev.target.value }) });
        return (
          <>
            <TextField label="Brand name" required {...f('brandName')} />
            <TextField label="Legal name" {...f('legalName')} />
            <TextField label="Primary contact email" type="email" {...f('primaryContactEmail')} />
            <TextField label="Phone" type="tel" {...f('phone')} />
            <TextAreaField label="Address" rows={2} {...f('address')} />
            <TextField label="Logo link" hint="An https:// link or a /path" {...f('logoUrl')} />
            <TextField label="Favicon link" hint="An https:// link or a /path" {...f('faviconUrl')} />
            <TextField label="Default social sharing image link" hint="Used when a page has no image of its own. An https:// link or a /path" {...f('defaultOgImageUrl')} />
            <fieldset className="rep-set">
              <legend>Social profiles</legend>
              {d.socialLinks.length === 0 ? <p className="ag-muted" style={{ margin: 0 }}>None yet.</p> : null}
              {d.socialLinks.map((s, i) => (
                <div className="rep" key={i}>
                  <TextField label={`Network (profile ${i + 1})`} required value={s.network} readOnly={readOnly} hint="For example LinkedIn" error={e[`socialLinks.${i}.network`]} onChange={(ev) => set({ ...d, socialLinks: d.socialLinks.map((x, k) => (k === i ? { ...x, network: ev.target.value } : x)) })} />
                  <TextField label={`Link (profile ${i + 1})`} required value={s.url} readOnly={readOnly} hint="Must start with https://" error={e[`socialLinks.${i}.url`]} onChange={(ev) => set({ ...d, socialLinks: d.socialLinks.map((x, k) => (k === i ? { ...x, url: ev.target.value } : x)) })} />
                  {!readOnly ? <RowControls noun="profile" index={i} count={d.socialLinks.length} onMove={(dir) => set({ ...d, socialLinks: moveItem(d.socialLinks, i, dir) })} onRemove={() => set({ ...d, socialLinks: d.socialLinks.filter((_, k) => k !== i) })} /> : null}
                </div>
              ))}
              {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={d.socialLinks.length >= 8 || undefined} onClick={() => d.socialLinks.length < 8 && set({ ...d, socialLinks: [...d.socialLinks, { network: '', url: '' }] })}>Add profile</Button>{d.socialLinks.length >= 8 ? <span className="ag-hint"> Up to 8.</span> : null}</div> : null}
            </fieldset>
          </>
        );
      }}
    </SettingEditor>
  );
}

// ---------- Navigation ----------
type NavChild = Navigation['items'][number]['children'][number];
type NavDraft = { items: { label: string; href: string; children: NavChild[] }[]; primaryCta: LinkRow; hasSecondary: boolean; secondaryCta: LinkRow };
export function NavigationForm() {
  return (
    <SettingEditor<Navigation, NavDraft>
      settingKey="navigation" title="Navigation" intro="The header menu and its call-to-action buttons. Menu items can have a dropdown of links."
      schema={navigationSchema}
      toDraft={(n) => ({ items: n.items.map((i) => ({ label: i.label, href: i.href, children: i.children })), primaryCta: n.primaryCta, hasSecondary: !!n.secondaryCta, secondaryCta: n.secondaryCta ?? { label: '', href: '' } })}
      fromDraft={(d) => ({ items: d.items, primaryCta: d.primaryCta, secondaryCta: d.hasSecondary ? d.secondaryCta : undefined })}
    >
      {({ draft: d, set, errors: e, readOnly }) => {
        const setItem = (i: number, n: NavDraft['items'][number]) => set({ ...d, items: d.items.map((x, k) => (k === i ? n : x)) });
        return (
          <>
            <fieldset className="rep-set">
              <legend>Menu items</legend>
              {d.items.length === 0 ? <p className="ag-muted" style={{ margin: 0 }}>No menu items yet.</p> : null}
              {d.items.map((it, i) => (
                <div className="rep" key={i}>
                  <TextField label={`Label (menu item ${i + 1})`} required value={it.label} readOnly={readOnly} error={e[`items.${i}.label`]} onChange={(ev) => setItem(i, { ...it, label: ev.target.value })} />
                  <TextField label={`Link (menu item ${i + 1})`} required value={it.href} readOnly={readOnly} hint="An https:// link, a /path or a #anchor" error={e[`items.${i}.href`]} onChange={(ev) => setItem(i, { ...it, href: ev.target.value })} />
                  <LinkListEditor legend={`Dropdown links (menu item ${i + 1})`} noun="dropdown link" context={`menu item ${i + 1}`} links={it.children} max={12} readOnly={readOnly} errors={e} errPrefix={`items.${i}.children`} onChange={(children) => setItem(i, { ...it, children })}
                    hint="Optional: a description turns a link into a mega-menu card; one link can be featured."
                    extra={(c, k, update) => {
                      const ctx = `dropdown link ${k + 1}, menu item ${i + 1}`;
                      return (
                        <>
                          <TextField label={`Description (${ctx})`} value={c.description ?? ''} readOnly={readOnly} error={e[`items.${i}.children.${k}.description`]} onChange={(ev) => update({ ...c, description: ev.target.value || undefined })} />
                          <SelectField label={`Icon (${ctx})`} value={c.icon ?? ''} disabled={readOnly} onChange={(ev) => update({ ...c, icon: (ev.target.value || undefined) as NavChild['icon'] })}>
                            <option value="">None</option>
                            {iconKeys.map((key) => <option key={key} value={key}>{key.replace(/-/g, ' ')}</option>)}
                          </SelectField>
                          <TextField label={`Badge (${ctx})`} value={c.badge ?? ''} readOnly={readOnly} hint="Short and true, for example a typical duration." error={e[`items.${i}.children.${k}.badge`]} onChange={(ev) => update({ ...c, badge: ev.target.value || undefined })} />
                          <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={c.featured === true} disabled={readOnly} onChange={(ev) => update({ ...c, featured: ev.target.checked || undefined })} /> Featured card ({ctx})</label>
                        </>
                      );
                    }} />
                  {!readOnly ? <RowControls noun="menu item" index={i} count={d.items.length} onMove={(dir) => set({ ...d, items: moveItem(d.items, i, dir) })} onRemove={() => set({ ...d, items: d.items.filter((_, k) => k !== i) })} /> : null}
                </div>
              ))}
              {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={d.items.length >= 10 || undefined} onClick={() => d.items.length < 10 && set({ ...d, items: [...d.items, { label: '', href: '', children: [] }] })}>Add menu item</Button>{d.items.length >= 10 ? <span className="ag-hint"> Up to 10.</span> : null}</div> : null}
            </fieldset>
            <fieldset className="rep-set">
              <legend>Primary button</legend>
              <TextField label="Label (primary button)" required value={d.primaryCta.label} readOnly={readOnly} error={e['primaryCta.label']} onChange={(ev) => set({ ...d, primaryCta: { ...d.primaryCta, label: ev.target.value } })} />
              <TextField label="Link (primary button)" required value={d.primaryCta.href} readOnly={readOnly} error={e['primaryCta.href']} onChange={(ev) => set({ ...d, primaryCta: { ...d.primaryCta, href: ev.target.value } })} />
            </fieldset>
            <fieldset className="rep-set">
              <legend>Secondary button</legend>
              <CheckField label="Show a secondary button" checked={d.hasSecondary} disabled={readOnly} onChange={(v) => set({ ...d, hasSecondary: v })} />
              {d.hasSecondary ? (
                <>
                  <TextField label="Label (secondary button)" required value={d.secondaryCta.label} readOnly={readOnly} error={e['secondaryCta.label']} onChange={(ev) => set({ ...d, secondaryCta: { ...d.secondaryCta, label: ev.target.value } })} />
                  <TextField label="Link (secondary button)" required value={d.secondaryCta.href} readOnly={readOnly} error={e['secondaryCta.href']} onChange={(ev) => set({ ...d, secondaryCta: { ...d.secondaryCta, href: ev.target.value } })} />
                </>
              ) : null}
            </fieldset>
          </>
        );
      }}
    </SettingEditor>
  );
}

// ---------- Footer ----------
type FooterDraft = { columns: { title: string; links: LinkRow[] }[]; legalLinks: LinkRow[]; note: string };
export function FooterForm() {
  return (
    <SettingEditor<Footer, FooterDraft>
      settingKey="footer" title="Footer" intro="Link columns, legal links and an optional note at the bottom of every page."
      schema={footerSchema}
      toDraft={(f) => ({ columns: f.columns, legalLinks: f.legalLinks, note: str(f.note) })}
      fromDraft={(d) => ({ columns: d.columns, legalLinks: d.legalLinks, note: orUndef(d.note) })}
    >
      {({ draft: d, set, errors: e, readOnly }) => (
        <>
          <fieldset className="rep-set">
            <legend>Columns</legend>
            {d.columns.length === 0 ? <p className="ag-muted" style={{ margin: 0 }}>No columns yet.</p> : null}
            {d.columns.map((c, i) => (
              <div className="rep" key={i}>
                <TextField label={`Title (column ${i + 1})`} required value={c.title} readOnly={readOnly} error={e[`columns.${i}.title`]} onChange={(ev) => set({ ...d, columns: d.columns.map((x, k) => (k === i ? { ...x, title: ev.target.value } : x)) })} />
                <LinkListEditor legend={`Links (column ${i + 1})`} noun="link" context={`column ${i + 1}`} links={c.links} max={12} readOnly={readOnly} errors={e} errPrefix={`columns.${i}.links`} onChange={(links) => set({ ...d, columns: d.columns.map((x, k) => (k === i ? { ...x, links } : x)) })} />
                {!readOnly ? <RowControls noun="column" index={i} count={d.columns.length} onMove={(dir) => set({ ...d, columns: moveItem(d.columns, i, dir) })} onRemove={() => set({ ...d, columns: d.columns.filter((_, k) => k !== i) })} /> : null}
              </div>
            ))}
            {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={d.columns.length >= 5 || undefined} onClick={() => d.columns.length < 5 && set({ ...d, columns: [...d.columns, { title: '', links: [] }] })}>Add column</Button>{d.columns.length >= 5 ? <span className="ag-hint"> Up to 5.</span> : null}</div> : null}
          </fieldset>
          <LinkListEditor legend="Legal links" noun="legal link" links={d.legalLinks} max={6} readOnly={readOnly} errors={e} errPrefix="legalLinks" hint="Privacy, cookies, terms and similar." onChange={(legalLinks) => set({ ...d, legalLinks })} />
          <TextAreaField label="Note" rows={2} value={d.note} readOnly={readOnly} hint={`${d.note.length}/240`} error={e.note} onChange={(ev) => set({ ...d, note: ev.target.value })} />
        </>
      )}
    </SettingEditor>
  );
}

// ---------- Default SEO ----------
type SeoDraft = { titleSuffix: string; description: string; robots: 'index' | 'noindex' };
export function DefaultSeoForm() {
  return (
    <SettingEditor<DefaultSeo, SeoDraft>
      settingKey="defaultSeo" title="Default SEO" intro="Site-wide search defaults. A page’s own title, description and robots setting take precedence where it has one."
      schema={defaultSeoSchema}
      toDraft={(s) => ({ titleSuffix: str(s.titleSuffix), description: str(s.description), robots: s.robots })}
      fromDraft={(d) => ({ titleSuffix: orUndef(d.titleSuffix), description: orUndef(d.description), robots: d.robots })}
    >
      {({ draft: d, set, errors: e, readOnly }) => (
        <>
          <TextField label="Title suffix" value={d.titleSuffix} readOnly={readOnly} hint="Appended to a page title that does not already contain it, for example “| Agency”. Up to 40 characters." error={e.titleSuffix} onChange={(ev) => set({ ...d, titleSuffix: ev.target.value })} />
          <TextAreaField label="Default description" rows={3} value={d.description} readOnly={readOnly} hint={`${d.description.length}/170`} error={e.description} onChange={(ev) => set({ ...d, description: ev.target.value })} />
          <SelectField label="Default indexing" value={d.robots} disabled={readOnly} error={e.robots} onChange={(ev) => set({ ...d, robots: ev.target.value as SeoDraft['robots'] })}>
            <option value="index">Index (allow search engines)</option>
            <option value="noindex">Noindex (ask search engines to stay away)</option>
          </SelectField>
          {d.robots === 'noindex' ? <Notice tone="warning" title="Noindex is a site-wide default" live={false}>Use this only for a site that is not ready for search, such as staging.</Notice> : null}
        </>
      )}
    </SettingEditor>
  );
}
